const DEFAULT_BUCKET_HOST = 'images-coachify.s3.us-east-2.amazonaws.com';

const PUBLIC_IMAGE_KEY_PREFIXES = [
  'coach-covers/',
  'listing-cover-photos/',
  'listing-cover/',
  'listing-images/',
  'listings/',
  'listing-',
  'subscription-tiers/',
  'images/session-types/',
];

export const isCoachifyS3Url = url =>
  typeof url === 'string' &&
  (url.includes(DEFAULT_BUCKET_HOST) ||
    /images-coachify[^/]*\.s3\.[^/]+\.amazonaws\.com/i.test(url));

export const normalizeCoachifyImageUrl = url => {
  if (!url || typeof url !== 'string') return url;

  const trimmed = url.trim();
  if (!trimmed) return url;

  if (isCoachifyS3Url(trimmed)) {
    return trimmed;
  }

  const key = trimmed.replace(/^\/+/, '');
  if (PUBLIC_IMAGE_KEY_PREFIXES.some(prefix => key.startsWith(prefix))) {
    return `https://${DEFAULT_BUCKET_HOST}/${key}`;
  }

  return url;
};

export const normalizeCoachifyImageUrls = urls =>
  Array.isArray(urls) ? urls.map(normalizeCoachifyImageUrl) : urls;

const isPublicCoachifyImageKey = value => {
  if (!value || typeof value !== 'string') return false;
  const key = value.trim().replace(/^\/+/, '');
  return PUBLIC_IMAGE_KEY_PREFIXES.some(prefix => key.startsWith(prefix));
};

const extractKeyFromCoachifyUrl = url => {
  if (!url || typeof url !== 'string') return null;
  const withoutQuery = url.trim().split('?')[0];
  if (!withoutQuery) return null;
  try {
    const urlObj = new URL(withoutQuery);
    const pathKey = urlObj.pathname.replace(/^\/+/, '');
    return pathKey || null;
  } catch {
    const match = withoutQuery.match(/images-coachify[^/]*\.s3\.[^/]+\.amazonaws\.com\/(.+)$/i);
    return match?.[1] || null;
  }
};

export const resolveCoachifyImageFromKey = (imageUrl, imageKey) => {
  const key = typeof imageKey === 'string' ? imageKey.trim().replace(/^\/+/, '') : '';

  // Keep presigned or canonical coachify URLs from API responses instead of rebuilding from key.
  if (imageUrl && typeof imageUrl === 'string') {
    const trimmed = imageUrl.trim();
    if (trimmed) {
      if (isCoachifyS3Url(trimmed)) {
        return {
          imageUrl: trimmed,
          imageKey: key || extractKeyFromCoachifyUrl(trimmed) || null,
        };
      }
      if (isPublicCoachifyImageKey(trimmed)) {
        const normalizedKey = trimmed.replace(/^\/+/, '');
        return {
          imageUrl: `https://${DEFAULT_BUCKET_HOST}/${normalizedKey}`,
          imageKey: key || normalizedKey,
        };
      }
    }
  }

  if (key && isPublicCoachifyImageKey(key)) {
    return {
      imageUrl: `https://${DEFAULT_BUCKET_HOST}/${key}`,
      imageKey: key,
    };
  }

  const normalizedUrl = imageUrl ? normalizeCoachifyImageUrl(imageUrl) : null;
  return {
    imageUrl: normalizedUrl,
    imageKey: key || null,
  };
};

export const normalizePublicDataImageFields = (publicData = {}) => {
  if (!publicData || typeof publicData !== 'object') return publicData;

  const fields = [
    'coverImageUrl',
    'bannerImageUrl',
    'profileBannerImageUrl',
    'coverPhotoUrl',
    'coverPhoto',
    'coverImage',
    'image',
    'imageUrl',
    'thumbnail',
  ];

  const next = { ...publicData };
  fields.forEach(field => {
    if (typeof next[field] === 'string' && next[field]) {
      next[field] = normalizeCoachifyImageUrl(next[field]);
    }
  });

  if (Array.isArray(next.images)) {
    next.images = normalizeCoachifyImageUrls(next.images);
  }
  if (Array.isArray(next.photos)) {
    next.photos = normalizeCoachifyImageUrls(next.photos);
  }

  const coverPair = resolveCoachifyImageFromKey(next.coverImageUrl, next.coverImageKey || next.bannerImageKey);
  if (next.coverImageUrl || next.coverImageKey || next.bannerImageKey) {
    next.coverImageUrl = coverPair.imageUrl;
    next.coverImageKey = coverPair.imageKey;
    if (!next.profileBannerImageUrl && coverPair.imageUrl) {
      next.profileBannerImageUrl = coverPair.imageUrl;
    }
  }

  return next;
};
